import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import OnboardingRouter from './components/onboarding/OnboardingRouter';
import Layout from './components/layout/LayoutNew';
import DashboardNew from './components/dashboard/DashboardNew';
import CustomerList from './components/customers/CustomersPage';
import CustomerDetail from './components/customers/CustomerDetail';
import CustomerForm from './components/customers/CustomerForm';
import QuotesPage from './components/quotes/QuotesPage';
import QuoteDetail from './components/quotes/QuoteDetail';

import QuoteReviewSend from './components/quotes/QuoteReviewSend';
import RecordDecisionPage from './components/quotes/RecordDecisionPage';
import ConvertedQuoteGuard from './components/quotes/ConvertedQuoteGuard';
import ChangeOrderReviewSend from './components/changeorders/ChangeOrderReviewSend';
import QuoteBuilderPage from './components/quotes/builder/QuoteBuilderPage';
import JobsPage from './components/jobs/JobsPage';
import JobDetail from './components/jobs/JobDetail';
import SuperAdminGuard from './components/admin/SuperAdminGuard';
import DealerConsole from './components/admin/DealerConsole';
import DealerDetail from './components/admin/DealerDetail';
import { DealerWorkspaceProvider } from './components/admin/DealerWorkspaceContext';
import MasterDefaults from './components/admin/MasterDefaults';
import SuperAdminReports from './components/admin/SuperAdminReports';
import AddAccountPage from './components/admin/AddAccountPage';
import JobForm from './components/jobs/JobForm';
import WorkOrderDetail from './components/workorders/WorkOrderDetail';
import ChangeOrderDetail from './components/changeorders/ChangeOrderDetail';
import ChangeOrderBuilderPage from './components/changeorders/builder/ChangeOrderBuilderPage';
import NewChangeOrderPage from './components/changeorders/NewChangeOrderPage';
import CalendarPage from './components/calendar/CalendarPage';
import InvoiceDetail from './components/invoices/InvoiceDetail';
import InvoiceReviewSend from './components/invoices/InvoiceReviewSend';
import SettingsLayout from './components/settings/SettingsLayout';
import SettingsNotFound from './components/settings/SettingsNotFound';
import SuperAdminNotFound from './components/admin/SuperAdminNotFound';
import CompanyProfilePage from './components/settings/CompanyProfilePage';
import UsersRolesPage from './components/settings/UsersRolesPage';
import QuoteAreasPage from './components/settings/QuoteAreasPage';
import LeadSourcesPage from './components/settings/LeadSourcesPage';
import CompetitorsPage from './components/settings/CompetitorsPage';
import CustomerTypesPage from './components/settings/CustomerTypesPage';
import JobWOTypesSettings from './components/settings/JobWOTypesSettings';
import ResourceTypesPage from './components/settings/ResourceTypesPage';
// Iter18: MultiOfficePage delisted — offices editor absorbed into Company Profile.
// Import retained (commented) in case a future super-admin flow needs it.
// import MultiOfficePage from './components/settings/MultiOfficePage';
import VendorsWarehousesPage from './components/settings/VendorsWarehousesPage';
import DocumentsPage from './components/settings/DocumentsPage';
import MarginsPage from './components/settings/MarginsPage';
import QuoteItemsPage from './components/settings/QuoteItemsPage';
import TaxRatesPage from './components/settings/TaxRatesPage';
import BrandingPage from './components/settings/BrandingPage';
import QuoteDefaultsPage from './components/settings/QuoteDefaultsPage';
import QuoteTemplatesPage from './components/settings/QuoteTemplatesPage';
import TCTemplatesPage from './components/settings/TCTemplatesPage';
import NotificationsPage from './components/settings/NotificationsPage';
import BillingPage from './components/settings/BillingPage';
import SupportPage from './components/settings/SupportPage';
import ReportsPage from './components/reports/ReportsPage';
import LeadsPage from './components/leads/LeadsPage';
import HelpCenterPage from './components/help/HelpCenterPage';
import HelpArticlePage from './components/help/HelpArticlePage';
import HelpBucketPage from './components/help/HelpBucketPage';
import FeedbackPage from './components/help/FeedbackPage';
import ErrorPage from './components/common/ErrorPage';
import LoginPage from './pages/Login';
import Profile from './pages/Profile';
import ChangePassword from './pages/ChangePassword';
import AccountClaim from './components/auth/AccountClaim';
import ResetPassword from './components/auth/ResetPassword';
import SignaturePage from './components/signature/SignaturePage';
import { ToastProvider, ConfirmProvider } from './components/ui';
import VersionGateBanner from './components/ui/VersionGateBanner';
import { SettingsProvider } from './hooks/useSettings';
import { isFieldTech, isFieldTechPath, landingPath, FIELD_TECH_HOME } from './utils/isFieldTech';
import { can } from './utils/permissions';
import './index.css';

// Dev-only: lazy-loaded so it's code-split out of the production bundle
const ComponentsShowcase = React.lazy(() => import('./components/dev/ComponentsShowcase'));
const CrewsPage = React.lazy(() => import('./components/settings/CrewsPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

// Route guard — redirects to /login if not authenticated. IDI-659 D1: setup
// is entered from the Dashboard's card only; nothing forces /onboarding.
function ProtectedRoute() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
}

// IDI-582 S1/D2: a field tech reaches only Jobs, a job, a work order,
// Calendar, a customer, profile, change-password and help. Any other URL
// sends them to /jobs (replace, no message). Paid users pass straight through.
function FieldTechGate() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (isFieldTech(user) && !isFieldTechPath(pathname)) {
    return <Navigate to={FIELD_TECH_HOME} replace />;
  }
  return <Outlet />;
}

// IDI-762 D4: "View reports" off sends Reports' addresses (and /invoices,
// which redirects there) to the Dashboard (replace, no message).
function ReportsGate() {
  const { user } = useAuth();
  if (!can(user, 'reports.view')) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
}

// IDI-582 S1: the app root lands a field tech on Jobs; everyone else keeps
// the existing landing.
function IndexLanding() {
  const { user } = useAuth();
  return <Navigate to={isFieldTech(user) ? landingPath(user) : '/quotes'} replace />;
}

// IDI-63: the Settings areas, shared by a dealer's own /settings and a
// super-admin's /super-admin/dealers/:id/settings workspace. `base` keeps the
// redirects inside whichever one is open (relative targets resolve against
// the enclosing settings route).
function settingsAreaRoutes() {
  return (
    <>
      <Route index element={<Navigate to="company" replace />} />
      <Route path="company" element={<CompanyProfilePage />} />
      <Route path="users" element={<UsersRolesPage />} />
      <Route path="quote-areas" element={<QuoteAreasPage />} />
      <Route path="lead-sources" element={<LeadSourcesPage />} />
      <Route path="competitors" element={<CompetitorsPage />} />
      <Route path="customer-types" element={<CustomerTypesPage />} />
      <Route path="job-types" element={<JobWOTypesSettings />} />
      <Route path="resource-types" element={<ResourceTypesPage />} />
      {/* Iter18: Multi-Office absorbed into Company Profile (§2.1). Redirect any
          stale /settings/offices links to the offices section of Company Profile. */}
      <Route path="offices" element={<Navigate to="../company#offices" replace />} />
      <Route path="vendors" element={<VendorsWarehousesPage />} />
      <Route path="documents" element={<DocumentsPage />} />
      <Route path="margins" element={<MarginsPage />} />
      <Route path="quote-items" element={<QuoteItemsPage />} />
      {/* IDI-507 (11): the old Item Catalog page is retired. Its CSV import
          wrote settings.catalog_items, which nothing reads. The live
          Item Catalog is /settings/quote-items. */}
      <Route path="item-catalog" element={<Navigate to="../quote-items" replace />} />
      <Route path="tax" element={<TaxRatesPage />} />
      <Route path="branding" element={<BrandingPage />} />
      <Route path="quote-defaults" element={<QuoteDefaultsPage />} />
      <Route path="quote-templates" element={<QuoteTemplatesPage />} />
      <Route path="tc-templates" element={<TCTemplatesPage />} />
      <Route path="notifications" element={<NotificationsPage />} />
      <Route path="billing" element={<BillingPage />} />
      <Route path="support" element={<SupportPage />} />
      <Route path="crews" element={<React.Suspense fallback={<div className="p-4">Loading...</div>}><CrewsPage /></React.Suspense>} />
      {/* UX-12 (2026-08-17 ruling): a bad /settings/* sub-route
          previously dropped through to the global 404 outside
          SettingsLayout, leaving the sub-nav gone and Settings
          still highlighted in the main nav. This wildcard renders
          SettingsNotFound INSIDE the layout's outlet so the rail
          stays and the user has a one-click way back in. */}
      <Route path="*" element={<SettingsNotFound />} />
    </>
  );
}

// IDI-622 (D4): the whole dealer page (every tab, Settings included) sits in
// that dealer's workspace, so its reads land in IDI-63's per-dealer caches and
// the top bar shows the dealer's company name. Settings reuses this provider.
function DealerPage() {
  const { id } = useParams();
  return (
    <DealerWorkspaceProvider key={id} dealerId={id}>
      <DealerDetail />
    </DealerWorkspaceProvider>
  );
}

function DealerSettingsWorkspace() {
  return <SettingsLayout />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AuthProvider>
          <ToastProvider>
          <VersionGateBanner />
          <ConfirmProvider>
          <SettingsProvider>
          <Router
            future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
          >
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/onboarding" element={<OnboardingRouter />} />
              <Route path="/claim" element={<AccountClaim />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/signature/:token" element={<SignaturePage />} />
              {/* Dev-only: visual showcase of every Iter-8 shared component.
                  Gated to development builds only — never ships to production. */}
              {process.env.NODE_ENV === 'development' && (
                <Route path="/components" element={<React.Suspense fallback={<div className="p-4">Loading...</div>}><ComponentsShowcase /></React.Suspense>} />
              )}

              {/* Protected routes */}
              <Route element={<ProtectedRoute />}>
                <Route element={<FieldTechGate />}>
                <Route element={<Layout />}>
                  <Route index element={<IndexLanding />} />
                  <Route path="dashboard" element={<DashboardNew />} />

                {/* Customers */}
                <Route path="customers" element={<CustomerList />} />
                <Route path="customers/:id" element={<CustomerDetail />} />
                <Route path="customers/:id/edit" element={<CustomerForm />} />

                {/* Leads */}
                <Route path="leads" element={<LeadsPage />} />

                {/* Quotes */}
                <Route path="quotes" element={<QuotesPage />} />
                <Route path="quotes/new" element={<Navigate to="/quotes/add" replace />} />
                {/* IDI-157: one guard on every quote-shaped page — a converted
                    quote (convertedJobId, any status) lands on its job before
                    any of these pages mounts.
                    IDI-564: /quotes/add is wrapped too (the guard passes
                    straight through with no id) so add and :id/edit render the
                    SAME element tree — the first autosave's add→edit navigate
                    then keeps one builder instance (IDI-332: no remount). */}
                <Route path="quotes/add" element={<ConvertedQuoteGuard><QuoteBuilderPage /></ConvertedQuoteGuard>} />
                <Route path="quotes/:id" element={<ConvertedQuoteGuard><QuoteDetail /></ConvertedQuoteGuard>} />
                <Route path="quotes/:id/edit" element={<ConvertedQuoteGuard><QuoteBuilderPage /></ConvertedQuoteGuard>} />
                <Route path="quotes/:id/review" element={<ConvertedQuoteGuard><QuoteReviewSend /></ConvertedQuoteGuard>} />
                <Route path="quotes/:id/record-decision" element={<ConvertedQuoteGuard><RecordDecisionPage /></ConvertedQuoteGuard>} />
                <Route path="change-orders/:id/review" element={<ChangeOrderReviewSend />} />

                {/* Jobs */}
                <Route path="jobs" element={<JobsPage />} />
                <Route path="jobs/:id" element={<JobDetail />} />
                <Route path="jobs/:id/edit" element={<JobForm />} />

                {/* Work Orders */}
                <Route path="work-orders/:id" element={<WorkOrderDetail />} />

                {/* Change Orders */}
                <Route path="change-orders/:id" element={<ChangeOrderDetail />} />
                {/* PT-43.1 (2026-07-28): create-then-redirect entry so the
                    "Create Change Order" affordance from the PT-42 document_locked
                    dialog has somewhere to point. Real work happens on the /build
                    route below — this component POSTs the CO and forwards. */}
                <Route path="jobs/:jobId/change-orders/new" element={<NewChangeOrderPage />} />
                <Route path="jobs/:jobId/change-orders/:coId/build" element={<ChangeOrderBuilderPage />} />

                {/* Calendar */}
                <Route path="calendar" element={<CalendarPage />} />

                {/* F8: Invoices — no standalone list, create from job context */}
                <Route element={<ReportsGate />}>
                  <Route path="invoices" element={<Navigate to="/reports?report=revenue" replace />} />
                </Route>
                <Route path="invoices/:id" element={<InvoiceDetail />} />
                <Route path="jobs/:jobId/invoices/new" element={<InvoiceReviewSend />} />
                {/* PT-34.3 (2026-07-28): existing invoice review route. Loads
                    via invoiceAPI.getInvoice(id); does NOT create a duplicate
                    draft on entry. Same component, different mode. */}
                <Route path="invoices/:invoiceId/review" element={<InvoiceReviewSend />} />

                {/* Settings */}
                <Route path="settings" element={<SettingsLayout />}>
                  {settingsAreaRoutes()}
                </Route>
                <Route element={<ReportsGate />}>
                  <Route path="reports" element={<ReportsPage />} />
                </Route>

                {/* Profile */}
                <Route path="profile" element={<Profile />} />
                <Route path="change-password" element={<ChangePassword />} />

                {/* Help */}
                <Route path="help" element={<HelpCenterPage />} />
                <Route path="help/bucket/:bucketId" element={<HelpBucketPage />} />
                <Route path="help/article/:articleId" element={<HelpArticlePage />} />
                <Route path="help/feedback" element={<FeedbackPage />} />

                {/* Super-Admin (D1: tenant-gated) */}
                <Route path="super-admin" element={<SuperAdminGuard />}>
                  <Route index element={<Navigate to="/super-admin/dealers" replace />} />
                  <Route path="dealers" element={<DealerConsole />} />
                  <Route path="dealers/:id" element={<DealerPage />}>
                    <Route path="settings" element={<DealerSettingsWorkspace />}>
                      {settingsAreaRoutes()}
                    </Route>
                  </Route>
                  <Route path="master-defaults" element={<MasterDefaults />} />
                  <Route path="reports" element={<SuperAdminReports />} />
                  <Route path="accounts/new" element={<AddAccountPage />} />
                  {/* PR-43 (§3): a bad /super-admin/* sub-route previously
                      fell through to the global 404 outside SuperAdminGuard,
                      leaving the main nav still highlighted with no way back
                      in. Sibling of PR-39's settings catch-all. */}
                  <Route path="*" element={<SuperAdminNotFound />} />
                </Route>

                {/* 404 */}
                <Route path="*" element={<ErrorPage statusCode={404} />} />
                </Route>
                </Route>
              </Route>
            </Routes>
          </Router>
          </SettingsProvider>
          </ConfirmProvider>
          </ToastProvider>
        </AuthProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;
